import moment from 'moment-timezone';

/**
 * Extract a standard 10-digit US phone number
 *
 * @param {string} raw the raw input
 * @param {boolean} acceptShortCode whether to accept 5-digit shortcodes
 */
export const GetPhoneFromInput = (
  raw: string,
  acceptShortCode?: boolean
): string => {
  if (!raw) {
    return;
  }
  let phone = null;
  const sanitized = raw.replace(/[^0-9+]/gi, '');
  const digitOnly = sanitized.replace(/[^0-9]/gi, '');
  // Handle: +1XXXXXXXXXX
  if (sanitized.length === 12 && sanitized.substring(0, 2) === '+1') {
    phone = sanitized.substring(2);
    // Handle: XXXXXXXXXX
  } else if (digitOnly.length === 10) {
    phone = digitOnly;
    // Handle: 1XXXXXXXXXX
  } else if (digitOnly.length === 11 && digitOnly[0] === '1') {
    phone = digitOnly.substring(1);
  } else if (acceptShortCode && digitOnly.length === 5) {
    phone = digitOnly;
  }
  return phone;
};

/**
 * Format a phone number to display format: (XXX) YYYY-ZZZZ
 *
 * @param {string} phone the phone number to format
 */
export const FormatPhone = (
  phone: string,
  acceptShortCode?: boolean
): string => {
  if (!phone || phone.trim().length === 0) {
    return '';
  }
  phone = GetPhoneFromInput(phone, acceptShortCode);
  if (!phone) {
    return '';
  }
  if (acceptShortCode && phone.length === 5) {
    phone = phone.replace(/(\d{3})(\d{2})/, '$1‑$2');
  } else {
    phone = phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2‑$3');
  }
  phone = phone.replaceAll(' ', '\u00a0');
  return phone;
};

/**
 * Format a date of birth to display format: MM/DD/YYYY
 *
 * @param dob {string} the date of birth to format
 */
export const FormatDOB = (dob: string): string =>
  moment(dob).format('MM/DD/YYYY');

/**
 * Pad a string with character to statisfy length requirements
 *
 * @param value the value to pad
 * @param length the length of the desired output string
 * @param char the character to pad with
 * @param isRight whether to pad to the right
 */
export const PadString = (
  value: string | number,
  length: number,
  char: string,
  isRight?: boolean
): string => {
  let output = String(value);
  const n = length - output.length;
  if (n > 0) {
    for (let i = 0; i < n; i++) {
      output = isRight ? output + char : char + output;
    }
  }
  return output;
};

/**
 * Deserialize an array of numbers from a comma separated string
 *
 * @param value a string that contains comma separated numbers
 */
export const DeserializeNumbers = (value: string): number[] => {
  if (value == null) {
    return null;
  }
  if (value === '') {
    return [];
  }
  return value.split(',').map((day) => parseInt(day, 10));
};

/**
 * Capitalize a word
 *
 * @param word the word to capitalize
 */
export const Capitalize = (word: string): string => {
  if (!word) {
    return word;
  }
  return word[0].toUpperCase() + word.slice(1);
};

export const FormatTitle = (title: string): string => {
  const parts = title.split(' ');
  const output = parts.map((part) => Capitalize(part)).join(' ');
  return output;
};

/**
 * Format numbers with commas
 *
 * @param number the number to format
 */
export const FormatNumber = (number: number): string => {
  return number.toLocaleString('en-US');
};
